import type { EmailDocument, TemplateId, Selection } from "./types";
import {
  cloneDocument,
  cloneSelection,
  HISTORY_LIMIT,
  type HistorySnapshot,
} from "./history";
import { fillDocumentDefaults } from "./default-fill";
import {
  EDITOR_FLUSH_HISTORY,
  type EditorAction,
  type EditorSliceState,
} from "./redux/types";
import type * as mutations from "./mutations/index";

export type DocumentLifecycleState = {
  document: EmailDocument;
  templateId: TemplateId;
  past: HistorySnapshot[];
  future: HistorySnapshot[];
  selection: Selection;
  saveStatus: 'idle' | 'pending' | 'saving' | 'saved' | 'error';
};

export function cloneMutableState<T extends DocumentLifecycleState>(
  state: T,
): T {
  return {
    ...state,
    document: structuredClone(state.document),
    selection: structuredClone(state.selection),
    past: structuredClone(state.past),
    future: structuredClone(state.future),
  };
}

export function cloneEditorSlice(state: EditorSliceState): EditorSliceState {
  return {
    ...cloneMutableState(state),
    selectedBlockIds: [...state.selectedBlockIds],
    primaryBlockId: state.primaryBlockId,
    anchorBlockId: state.anchorBlockId,
    selectedRowIds: [...state.selectedRowIds],
    primaryRowId: state.primaryRowId,
    anchorRowId: state.anchorRowId,
    blockClipboard: state.blockClipboard.map(block => structuredClone(block)),
    textEditingBlock: state.textEditingBlock
      ? structuredClone(state.textEditingBlock)
      : null,
    textEditReentryGuard: state.textEditReentryGuard,
  };
}

let historyInteractionDepth = 0;
let historyInteractionBefore: HistorySnapshot | null = null;
type HistoryDispatch = (action: EditorAction) => void;
let historyInteractionDispatch: HistoryDispatch | null = null;

export function registerHistoryDispatch(dispatch: HistoryDispatch): void {
  historyInteractionDispatch = dispatch;
}

export function beginHistoryInteraction(): void {
  historyInteractionDepth += 1;
}

function flushHistoryInteraction(): void {
  if (!historyInteractionDispatch) {
    return;
  }
  const snapshot = historyInteractionBefore;
  historyInteractionBefore = null;
  if (!snapshot) {
    return;
  }
  historyInteractionDispatch({ type: EDITOR_FLUSH_HISTORY, payload: snapshot });
}

export function endHistoryInteraction(): void {
  if (historyInteractionDepth <= 0) {
    return;
  }
  historyInteractionDepth -= 1;
  if (historyInteractionDepth > 0) {
    return;
  }
  flushHistoryInteraction();
}

export function bumpHistoryInteraction(): void {
  if (historyInteractionDepth <= 0) {
    return;
  }
  endHistoryInteraction();
  beginHistoryInteraction();
}

export function ingestDocument(doc: EmailDocument): EmailDocument {
  return fillDocumentDefaults(doc);
}

function snapshotBeforeChange(
  documentBefore: EmailDocument,
  selectionBefore: Selection,
): HistorySnapshot {
  return {
    document: documentBefore,
    selection: cloneSelection(selectionBefore),
  };
}

export function applyDocumentCommit(
  state: EditorSliceState,
  run: (draft: mutations.EditorDraft) => boolean | void,
  options?: { history?: boolean },
): EditorSliceState {
  const next = cloneMutableState(state);
  const before = cloneDocument(next.document);
  const selectionBefore = next.selection;
  const result = run(next);
  if (result === false) {
    next.document = before;
    return next;
  }
  const recordHistory = options?.history !== false;
  if (historyInteractionDepth > 0) {
    if (recordHistory && !historyInteractionBefore) {
      historyInteractionBefore = snapshotBeforeChange(before, selectionBefore);
    }
    return next;
  }
  if (recordHistory) {
    next.past.push(snapshotBeforeChange(before, selectionBefore));
    if (next.past.length > HISTORY_LIMIT) {
      next.past.shift();
    }
    next.future = [];
  }
  return next;
}
